// Which models a conversation can be opened against, and what to call them on
// screen. Client-safe on purpose — `lib/runner.ts` is server-only, and the
// picker is a browser control.
//
// Keep the ids in lockstep with MODELS in runner/runner.py, which is what
// actually validates them; this list only decides what is offered.

export const MODELS = ['haiku', 'sonnet', 'opus'] as const;

export type Model = (typeof MODELS)[number];

/** The runner's default, and this app's. Kept here so the UI can show it before
 *  a session exists to ask about. */
export const DEFAULT_MODEL: Model = 'sonnet';

/**
 * What each one is for, in the order they are offered — cheapest first, so the
 * list reads as a ladder you climb deliberately rather than a menu with the
 * expensive option sitting at the top by default.
 *
 * The second line is the reason the picker exists at all. A model name alone
 * tells you nothing about what it costs, and "which of these three" is not a
 * question anyone should have to already know the answer to.
 */
export const MODEL_LABELS: Record<Model, { name: string; note: string }> = {
  haiku: { name: 'Haiku', note: 'Fastest and cheapest. Short questions, small edits.' },
  sonnet: { name: 'Sonnet', note: 'The default. Handles most of what happens here.' },
  opus: { name: 'Opus', note: 'Slowest and dearest. Worth it for hard problems.' },
};

/** A stored value the runner might not offer any more, kept safe to render. */
export function modelName(model: string | undefined): string {
  return MODEL_LABELS[model as Model]?.name ?? model ?? '';
}

export function isModel(value: unknown): value is Model {
  return typeof value === 'string' && (MODELS as readonly string[]).includes(value);
}
